import { ServicesType } from 'utils/globalTypes';

export const SERVICES = {
  alerts: 'alerts',
  vpnInTouch: 'vpnInTouch',
  passwordDefense: 'passwordDefense',
  dboo: 'dataBrokerOptOut',
  passwords: 'passwords',
  digitalVault: 'digitalVault',
  medicalDataMonitoring: 'medicalDataMonitoring',
  darkweb: 'darkweb',
  darkwebScanReport: 'darkwebScanReport',
  passwordScan: 'passwordScan',
  insurance: 'insurance',
  privateSearch: 'privateSearch',
  htmon: 'homeTitleMonitoring',
  publicRecords: 'publicRecordsMonitoring',
  // Synthesized client-side (see `selectUserFeatures`) — the runtime
  // dashboard treats every dminor user as entitled. BE does NOT send
  // a native entitlement for this. Angular parity:
  // `user-plan.service.ts:99-111` (runs on every session load and
  // pushes `public_record_trace` unconditionally on brand=dminor;
  // enrollment.service.ts has a stricter enrollment-only gate that
  // doesn't affect the runtime render).
  publicRecordTrace: 'publicRecordTrace',
  txmon: 'transactionMonitoring',
  creditScore: 'creditScore',
  creditScoreSimulator: 'creditScoreSimulator',
  creditLock: 'creditLock',
  creditReport: 'creditReport',
  medicalCannabisID: 'medicalCannabisID',
  cryptoWallet: 'cryptoWallet',
  creditReport3B: 'credit_report_3b',
  crtbOrchestration: 'crtb_orchestration',
  creditReportTBCR: 'creditReportTBCR',
  creditReport1B: 'credit_report_1b',
  creditReportFree: 'credit_report_free',
  bankAccountMonitoring: 'bankAccountMonitoring',
  walletProtection: 'walletProtection',
  antivirus: 'antivirus',
  financialCalculator: 'financial_calculator',
  socialMedia: 'socialMedia',
  securityFreeze: 'securityFreeze',
  spendingTracker: 'spendingTracker',
  healthLock: 'healthLock',
  insightsAndTips: 'insightsAndTips',
  familyPlan: 'familyPlan',
  passwordManager: 'passwordManager',
  // Angular parity — `@Feature(ClientFeature.authentication_alerts)`
  // on `identity-monitoring.component.ts:17`. Its own service key so
  // the Identity Monitoring page/menu is gated ONLY on the
  // `authentication_alerts` entitlement, not on the broader
  // `SERVICES.alerts` bundle (which fires for any of many alert
  // entitlements — htmon, txmon, credit, etc. — Angular does not
  // show Identity Monitoring for those).
  identityMonitoring: 'identityMonitoring',
} as const;

/**
 * Maps each service to the entitlements required for access.
 *
 * Rules:
 * - If the service value contains multiple entries, the user must have at least one of them.
 * - If a single entry contains comma-separated values, the user must have all of those entitlements.
 */
export const SERVICE_ENTITLEMENTS_MAPPING: Record<ServicesType, string[]> = {
  [SERVICES.alerts]: [
    'authentication_alerts',
    'fouriq',
    'healthcare_data',
    'htmon',
    'address_monitoring',
    'public_record_identity_misuse',
    'bama',
    'credit_monitoring_3b',
    'credit_monitoring_1b',
    'public_record_criminal',
    'public_record_sex_offender',
    'transaction_monitoring',
    'credit_lock',
    'credit_score',
  ],
  [SERVICES.vpnInTouch]: ['vpn_in_touch'],
  [SERVICES.passwordDefense]: ['pango_pwm'],
  [SERVICES.dboo]: ['dboo'],
  [SERVICES.passwords]: ['pango_pwm', 'vault'],
  [SERVICES.digitalVault]: ['pango_pwm', 'vault'],
  [SERVICES.medicalDataMonitoring]: ['fouriq, healthcare_data'],
  // TODO(entitlements): placeholder — mirrors Angular's itps:medical→'healthlock'.
  // Confirm the exact HealthLock entitlement code with the entitlements team.
  [SERVICES.healthLock]: ['healthlock'],
  [SERVICES.darkweb]: ['fouriq'],
  [SERVICES.darkwebScanReport]: ['dw_1x_scan'],
  [SERVICES.passwordScan]: ['password_scan'],
  [SERVICES.insurance]: ['insurance'],
  [SERVICES.privateSearch]: ['private_browser', 'private_search'],
  [SERVICES.htmon]: ['htmon'],
  [SERVICES.publicRecords]: ['prp', 'prm'],
  // Client-synthesised — pushed into `session.features` by
  // `selectUserFeatures` whenever brand === 'dminor'. Listing it
  // here lets `useIsFeatureAvailable(SERVICES.publicRecordTrace)`
  // and the dashboard `hasService(...)` predicate work uniformly.
  [SERVICES.publicRecordTrace]: ['public_record_trace'],
  [SERVICES.txmon]: ['ftm'],
  [SERVICES.creditScore]: ['efxvs', 'tuvs', 'xpnvs'],
  [SERVICES.creditScoreSimulator]: ['expsim'],
  [SERVICES.creditLock]: ['xpncl'],
  [SERVICES.creditReport3B]: ['credit_report', 'efxcrt', 'tuncr'],
  [SERVICES.crtbOrchestration]: ['crtb'],
  [SERVICES.creditReportTBCR]: ['tbcr'],
  [SERVICES.creditReport1B]: ['xpncr', 'efxcr', 'xpncrb'],
  [SERVICES.creditReportFree]: ['free_cr'],
  [SERVICES.creditReport]: [
    'tbcr',
    'efxcrt',
    'tuncr',
    'xpncr',
    'crtb',
    'free_cr',
    'efxcr',
    'credit_report',
  ],
  [SERVICES.medicalCannabisID]: ['fouriq, healthcare_data, medical_cannabis'],
  [SERVICES.cryptoWallet]: ['fouriq, crypto_wallet'],
  [SERVICES.bankAccountMonitoring]: ['bama'],
  [SERVICES.walletProtection]: ['wallet_protection'],
  [SERVICES.antivirus]: ['av'],
  [SERVICES.financialCalculator]: ['fincalc'],
  [SERVICES.socialMedia]: ['social_monitor'],
  [SERVICES.securityFreeze]: ['security_freeze'],
  [SERVICES.spendingTracker]: ['spending_tracker'],
  [SERVICES.insightsAndTips]: ['news_articles', 'feat_insight_tips'],
  // JWT entitlement `itps:can_invite: true` → sessionUtils.formatUserFeatures
  // pushes `'can_invite'` into `session.features`. This gate drives the
  // FamilyCard dashboard tile and the Add Member CTA on plan-members.
  [SERVICES.familyPlan]: ['can_invite'],
  [SERVICES.passwordManager]: ['password_manager_sp'],
  [SERVICES.identityMonitoring]: ['authentication_alerts'],
};
